import type {
  FieldDefinition,
  IssuedCredential,
  PersonDetail,
  PersonStatus,
  RecordActionMethod,
} from '@sentra/contracts';
import { ageOnDate, IMAGES_COMPLETENESS_KEY, translate } from '@sentra/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { hasCode } from '@/api/errors';
import { FailureAlert } from '@/api/failure';
import { applyWriteEffects } from '@/api/invalidation';
import { useApiMutation } from '@/api/mutations';
import { useDuplicateCandidates } from '@/api/queries';
import type { BodyOf } from '@/api/routes';
import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { notePersonWrite } from '@/api/write-clock';
import {
  useIsPreprinted,
  useNoBandOffered,
  useReferenceRequiredWithoutBand,
} from '@/app/providers/config';
import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';
import { useApiClient } from '@/features/auth/session';
import type { FieldMap } from '@/features/conflicts/conflict';
import { conflictsBetween } from '@/features/conflicts/conflict';
import { ConflictDialog } from '@/features/conflicts/conflict-dialog';
import { AdhocFields } from '@/features/persons/form/adhoc-fields';
import { rememberedNames, rememberNames } from '@/features/persons/form/adhoc-names';
import { BandField, useBandEntry } from '@/features/persons/form/band-field';
import { CompletenessDialog } from '@/features/persons/form/completeness-dialog';
import { DuplicateGuard } from '@/features/persons/form/duplicate-guard';
import { FieldControl } from '@/features/persons/form/field-control';
import { sectionHeading } from '@/features/persons/form/section-heading';
import { labelForFieldPath, serverFieldProblems } from '@/features/persons/form/server-problems';
import { StatusField } from '@/features/persons/form/status-field';
import type {
  AdhocDraft,
  FieldProblem,
  FormValue,
  FormValues,
} from '@/features/persons/form/values';
import {
  adhocProblem,
  blankValues,
  groupedFields,
  labelOf,
  submittableAdhoc,
  submittableFields,
  toFieldMap,
  validateAll,
  valuesFromFieldMap,
  valuesFromRecord,
} from '@/features/persons/form/values';
import type { MediaDevicesLike } from '@/features/persons/images/camera';
import type { CaptureCanvas } from '@/features/persons/images/capture';
import type { DeviceStorage } from '@/features/persons/images/devices';
import type { StagedPhotograph } from '@/features/persons/images/staged';
import { attachStagedPhotographs, releaseStaged } from '@/features/persons/images/staged';
import { StagedPhotographs } from '@/features/persons/images/staged-photographs';
import { useDebounced } from '@/features/persons/use-debounced';
import { useLocale } from '@/i18n';
import { useFormatters } from '@/i18n/use-format';

/**
 * The registration form: every field the installation has declared, and nothing hard-coded.
 *
 * **It is rendered from the live registry** (AD-16). There is no list of fields anywhere in this
 * package: the definitions arrive from `GET /api/v1/fields`, each carries its type, its label in
 * both languages, its help text, its requirement level and its validation record, and
 * `FieldControl` renders one control per type. An administrator who adds a field sees it here on the
 * next load, with no code change.
 *
 * **Nothing it refuses is a refusal the API would not make**, because the validators are the
 * contract's own. And nothing it accepts is submitted blank: an optional field left empty is omitted
 * from the request rather than sent as an empty string, which is what keeps the completeness warning
 * honest for the life of the record.
 *
 * **The completeness warning is a question, never a block** (Section 1.2). The server answers a
 * create that is missing warn-level fields with a 200 carrying `confirmation_required`, and this
 * screen turns that into a dialog naming the fields; confirming re-submits with `acceptIncomplete`.
 * The re-submission mints a NEW idempotency key, and that is correct rather than incidental: the
 * body differs, so reusing the first key would be refused as a reused key (AD-30), and a confirmed
 * submission genuinely is a second, deliberate act.
 *
 * **The duplicate guard warns while the operator types and never gates the submit button** (AD-18).
 * Dismissing it changes nothing about what can be saved, which the tests assert as a negative.
 *
 * **A 409 opens the field-level conflict dialog and nothing is resubmitted until every divergence
 * has been answered** (AD-15). The merge starts from the SAVED record, so a field the other person
 * changed and this one never touched is kept rather than silently overwritten.
 */

/** What the form is doing: registering somebody, or amending a record that already exists. */
/** The one core key this form treats specially, because the age is rendered beside it. */
const BIRTH_DATE_KEY = 'date_of_birth';

/**
 * The field a bandless registration must carry, read by the one cross-control rule on this screen.
 *
 * A key rather than a type or a section, for the reason `search/fields.ts` gives about the name
 * fields: it is seeded and unchangeable, where a section is free text an administrator can retype.
 */
const REFERENCE_FIELD_KEY = 'reference_code';

export type PersonFormMode =
  { readonly kind: 'create' } | { readonly kind: 'amend'; readonly person: PersonDetail };

/** What a completed create produced. The credential is `null` when the registration issued none. */
export interface CreatedPerson {
  readonly person: PersonDetail;
  readonly credential: IssuedCredential | null;
}

/** How long the duplicate guard waits after the last keystroke before it asks. */
const DUPLICATE_GUARD_DEBOUNCE_MS = 300;

/**
 * How much has to be typed before the duplicate guard asks anything.
 *
 * An empty form matches everybody, and a warning that fires on the first letter is one operators
 * learn to ignore — which would be the strongest signal in the product turned into noise.
 */
const GUARD_MIN_VALUE_LENGTH = 3;

/** Whether the guard has been given something worth asking about. */
function worthAsking(fields: Readonly<Record<string, unknown>>): boolean {
  return Object.values(fields).some(
    (value) => typeof value === 'string' && value.trim().length >= GUARD_MIN_VALUE_LENGTH,
  );
}

type CreateBody = BodyOf<typeof apiRoutes.personCreate>;
type AmendBody = BodyOf<typeof apiRoutes.personPatch>;

interface AmendInput {
  readonly id: string;
  readonly body: AmendBody;
}

/** What one submission carries beyond the form's own values. */
interface SubmitOptions {
  readonly acceptIncomplete: boolean;
  readonly consentAcknowledged?: boolean;
}

export function PersonForm({
  mode,
  definitions,
  method,
  initialBandCode = null,
  autoFocusFields = false,
  onCreated,
  onAmended,
  onCancel,
  storage = globalThis.localStorage,
  camera,
  leaveControl,
}: Readonly<{
  mode: PersonFormMode;
  definitions: readonly FieldDefinition[];
  /** AD-53: how this action was reached, which the audit row records. */
  method: RecordActionMethod;
  /**
   * A band the gate already scanned, seeded into the field (`features/scan/handoff.ts`).
   *
   * It is seeded rather than verified: the lookup still runs, because the gate's resolve and this
   * form's lookup are different questions asked of different tables, and the second is what catches
   * a band somebody else claimed in the seconds between. Only the typing is saved.
   */
  initialBandCode?: string | null;
  /**
   * Whether to put the caret in the first field once the registry has loaded.
   *
   * True only when the operator arrived from the gate, where the band is already filled and the
   * next thing they do is type a name with somebody standing in front of them. Focus moved by a
   * user-initiated navigation is neither WCAG 3.2.1 nor 3.2.2, both of which are about a context
   * change CAUSED by focus or input.
   */
  autoFocusFields?: boolean;
  onCreated?: (created: CreatedPerson) => void;
  onAmended?: (person: PersonDetail) => void;
  onCancel?: () => void;
  /** Where the ad-hoc name autocomplete remembers its labels. Injected so a test can hand in one. */
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  /**
   * The camera seams the staged photograph strip needs, none of which exist in `jsdom`.
   *
   * Its own member rather than more parameters on this component, and its `storage` is deliberately
   * NOT the one above: that one is where the ad-hoc autocomplete remembers labels, and a single
   * prop carrying two meanings is how a test that stubs one silently changes the other.
   */
  camera?: {
    readonly media?: MediaDevicesLike | null;
    readonly storage?: DeviceStorage;
    readonly createCanvas?: () => CaptureCanvas;
  };
  /**
   * The caller's way off this screen, rendered above the form and withdrawn once it is terminal.
   *
   * It is a PROP rather than something the caller renders beside this component, because only this
   * component knows when the record has been created: from that moment there is nothing left to
   * cancel, and leaving would strand a minted pass code that no operator can ever fetch back.
   */
  leaveControl?: ReactNode;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const format = useFormatters();
  const describe = useApiErrorMessage();
  const preprinted = useIsPreprinted();
  const offerNoBand = useNoBandOffered();
  const referenceRequiredWithoutBand = useReferenceRequiredWithoutBand();

  const groups = useMemo(() => groupedFields(definitions), [definitions]);
  const today = useMemo(() => format.calendarDate(new Date()), [format]);

  const [values, setValues] = useState<FormValues>(() =>
    mode.kind === 'amend'
      ? valuesFromRecord(definitions, mode.person.fields)
      : blankValues(definitions),
  );
  const [drafts, setDrafts] = useState<readonly AdhocDraft[]>(() =>
    mode.kind === 'amend' ? mode.person.adhocFields.map((row) => ({ ...row })) : [],
  );
  const [duplicatesDismissed, setDuplicatesDismissed] = useState(false);
  /**
   * The situation this amendment would leave the record in.
   *
   * Seeded from the record and sent with every amendment, which is what the service expects:
   * `input.status ?? existing.status`, with the archive guard scoped to the TRANSITION rather than
   * to the value, so re-sending an unchanged status is a no-op and `statusAction` writes a
   * transition row only when it actually moved.
   */
  const [status, setStatus] = useState<PersonStatus>(
    mode.kind === 'amend' ? mode.person.status : 'active',
  );
  /** The photographs this registration is carrying, which do not exist anywhere else yet. */
  const [staged, setStaged] = useState<readonly StagedPhotograph[]>([]);
  /**
   * The record this form has already created, once it has created one.
   *
   * Its presence is what makes the form terminal: the person exists, so there is nothing left to
   * submit and nothing left to cancel, and the only two ways out are retrying the photograph or
   * going on without it.
   */
  const [attaching, setAttaching] = useState<{
    readonly created: CreatedPerson;
    readonly error: unknown;
  } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [missing, setMissing] = useState<readonly string[] | null>(null);
  const [consentDismissed, setConsentDismissed] = useState(false);
  const [theirs, setTheirs] = useState<FieldMap | null>(null);
  // The version the merge was computed against, from the same read `theirs` came from. Until a
  // conflict has been resolved there is none, and the record's own version is what a first
  // amendment quotes.
  const [resolvedVersion, setResolvedVersion] = useState<number | null>(null);

  /**
   * The band this registration is offering, which in a pre-printed installation is REQUIRED.
   *
   * Scoped to a create, and that scope is load-bearing rather than tidy: an amendment names no band
   * and never could — `send` forwards `passCode` on the create branch alone — so a readiness test
   * that also bound an amendment would refuse every edit of every record in the default mode, and
   * `submit()` returns silently when it is blocked, so nothing on screen would say why.
   */
  const bandRequired = preprinted && mode.kind === 'create';
  const band = useBandEntry(bandRequired, initialBandCode);

  /**
   * The caret, put where the operator is about to type.
   *
   * It waits on `fields` rather than firing on mount, because the registry is fetched and the grid
   * is empty on the first render; and it fires once, because a definition list that settles in two
   * steps must not take the caret back off whatever they have already started typing. The band
   * field is deliberately skipped — it is already filled, and the operator does not need to confirm
   * it by hand: `band-field.tsx` announces the lookup's answer through its own `role="status"`.
   */
  const fieldsRef = useRef<HTMLDivElement | null>(null);
  const focused = useRef(false);
  useEffect(() => {
    if (!autoFocusFields || focused.current) {
      return;
    }
    const first = fieldsRef.current?.querySelector<HTMLElement>(
      'input:not([disabled]),select:not([disabled]),textarea:not([disabled])',
    );
    if (first === null || first === undefined) {
      return;
    }
    focused.current = true;
    first.focus();
  }, [autoFocusFields, groups]);
  const validationProblems = useMemo(
    () => validateAll(definitions, values, { today, locale }),
    [definitions, values, today, locale],
  );
  /**
   * The form's refusals, plus the one cross-control rule this screen owns.
   *
   * **A registration that declares no band must name a reference**, because the band is what would
   * otherwise make the record findable at a door and the reference is what replaces it. The server
   * refuses the same case with `PERSON_REFERENCE_REQUIRED` and this renders THAT refusal's own
   * sentence, from its code, so the two cannot come to say different things — the same arrangement
   * the band field has with `CREDENTIAL_CODE_REQUIRED`.
   *
   * **Guarded on the field being in the REGISTRY, not just on the mode.** `blocked` reads this map
   * and `submit()` returns silently when blocked, so attaching a refusal to a field this form is not
   * rendering — a browser talking to an api that booted before the field was seeded — would be a
   * Save nobody can complete with nothing on screen saying why. In that state the server's own
   * refusal still arrives, through `serverFieldProblems` and the banner.
   */
  const problems = useMemo((): FieldProblem => {
    if (!referenceRequiredWithoutBand || !band.noBand || mode.kind !== 'create') {
      return validationProblems;
    }
    const declared = definitions.some(
      (definition) => definition.key === REFERENCE_FIELD_KEY && definition.status === 'active',
    );
    const held = values[REFERENCE_FIELD_KEY];
    const filled = typeof held === 'string' && held.trim().length > 0;
    if (!declared || filled) {
      return validationProblems;
    }
    return {
      ...validationProblems,
      [REFERENCE_FIELD_KEY]: translate(locale, 'errors.person.reference_required'),
    };
  }, [
    validationProblems,
    referenceRequiredWithoutBand,
    band.noBand,
    mode.kind,
    definitions,
    values,
    locale,
  ]);
  const adhocFault = useMemo(() => adhocProblem(drafts), [drafts]);

  /**
   * One control's change handler, made per field key.
   *
   * A factory rather than an arrow written inline in the grid: the grid is now a map over sections
   * containing a map over fields, and a handler declared at that depth is four function levels from
   * the component — which the linter refuses, and rightly, because it is also the depth at which a
   * closure over the wrong loop variable stops being obvious.
   */
  const changeField = useCallback(
    (key: string) =>
      (next: FormValue): void => {
        setValues((held) => ({ ...held, [key]: next }));
      },
    [],
  );

  /**
   * The age beside the date of birth, computed rather than stored.
   *
   * An age typed into a field is wrong the day after it is entered and then disagrees with the
   * birthday next to it, so this product records the birthday and derives the number. `today` is
   * the same injected date every date bound on this form is checked against, cut in the
   * installation's reporting zone.
   */
  const derivedAge = useMemo(() => {
    const birth = values[BIRTH_DATE_KEY];
    return typeof birth === 'string' && birth.length > 0 ? ageOnDate(birth, today) : null;
  }, [values, today]);

  const suggestions = useMemo(() => {
    const onRecord = mode.kind === 'amend' ? mode.person.adhocFields.map((row) => row.name) : [];
    return [...new Set([...onRecord, ...rememberedNames(storage)])];
  }, [mode, storage]);

  const submittable = useMemo(() => submittableFields(definitions, values), [definitions, values]);
  const guardFields = useDebounced(submittable, DUPLICATE_GUARD_DEBOUNCE_MS);
  const candidates = useDuplicateCandidates(guardFields, {
    enabled: !duplicatesDismissed && worthAsking(guardFields),
    ...(mode.kind === 'amend' ? { excludePersonId: mode.person.id } : {}),
  });

  const remember = useCallback(() => {
    rememberNames(
      storage,
      submittableAdhoc(drafts).map((row) => row.name),
    );
  }, [drafts, storage]);

  const client = useApiClient();
  const queries = useQueryClient();

  /**
   * Attaching the staged photographs to the record that now exists.
   *
   * **It catches everything.** The promise is launched with `void` from a callback typed
   * `=> void`, so a rejection escaping it is an unhandled rejection: under the test conditions that
   * is a failed run, and in a browser it is silence where D5 promised the operator a sentence.
   *
   * **Each photograph that lands is dropped from the staged list.** Neither image route is behind
   * the idempotency middleware, so a retry that re-sent the whole list would store a second copy of
   * everything that had already committed.
   */
  /**
   * Forgetting one photograph the moment it commits.
   *
   * Its own callback rather than an arrow inside the attach, so that "what a retry re-sends" is one
   * named thing: everything still in `staged`, and nothing that has already landed.
   */
  const forgetAttached = useCallback((done: StagedPhotograph) => {
    releaseStaged(done);
    setStaged((held) => held.filter((one) => one.id !== done.id));
  }, []);

  /**
   * Everything still staged when this form goes away, released.
   *
   * A form abandoned with photographs on it — Cancel, or a gate-originated registration the operator
   * walks away from — would otherwise leave an object URL alive for the life of the tab, in a
   * long-lived single-page application on a terminal that is never reloaded. Held in a ref because
   * the cleanup must see what is staged AT UNMOUNT rather than what was staged when an effect last
   * ran, and written in an effect body rather than during a render.
   */
  const stagedRef = useRef<readonly StagedPhotograph[]>([]);
  useEffect(() => {
    stagedRef.current = staged;
  }, [staged]);
  useEffect(
    () => () => {
      for (const one of stagedRef.current) {
        releaseStaged(one);
      }
    },
    [],
  );

  const runAttach = useCallback(
    async (created: CreatedPerson, queued: readonly StagedPhotograph[]): Promise<void> => {
      try {
        await attachStagedPhotographs(client, created.person.id, queued, forgetAttached);
        // **The attach goes through the client directly, so it declares nothing on its own.** What a
        // photograph changes is the record's completeness — the very thing this screen was asked to
        // confirm a moment ago — and the register row's thumbnail. Applied for the LAST photograph
        // rather than per upload: they all land on one record, and the marks are identical.
        applyWriteEffects(queries, apiRoutes.personImageUpload, {
          params: { id: created.person.id },
          body: undefined,
          result: undefined,
        });
        // Only once the photographs are on the record, because the window this opens is five
        // seconds of the register polling for its own write, and two multipart uploads would eat
        // most of it before the operator ever reached the list.
        notePersonWrite();
        onCreated?.(created);
      } catch (error: unknown) {
        setAttaching({ created, error });
      }
    },
    [client, forgetAttached, onCreated, queries],
  );

  const create = useApiMutation<typeof apiRoutes.personCreate, CreateBody>(
    apiRoutes.personCreate,
    (body) => ({ body }),
    {
      onSuccess: (result) => {
        if (result.kind === 'confirmation_required') {
          // **The photograph in hand is not a missing photograph.** The server computes the warning
          // before the record exists, so `images` is in it on every single registration; asking an
          // operator to confirm they have not taken a photograph they just took is what teaches a
          // room to click past this dialog without reading it.
          const residual =
            staged.length > 0
              ? result.completeness.missing.filter((key) => key !== IMAGES_COMPLETENESS_KEY)
              : [...result.completeness.missing];
          if (residual.length === 0) {
            setMissing(null);
            // **Re-entrant, deliberately, and never deferred.** The mutation library awaits this
            // callback BEFORE it dispatches success, and `mutate` swaps the observer onto the
            // second request and re-asserts `pending` synchronously — so the Save button stays
            // disabled across the swap. Deferring through a microtask would open the one frame in
            // which it is enabled again, and a second press there is a second person with a second
            // active pass. This holds because `onSuccess` is an option of `useMutation`; a
            // per-call callback runs after the dispatch and would make the gap real.
            send({ acceptIncomplete: true });
            return;
          }
          setMissing(residual);
          return;
        }
        setMissing(null);
        remember();
        const created = { person: result.person, credential: result.credential };
        if (staged.length === 0) {
          // The register is fed by a projection a second or two behind, so the list this operator
          // goes back to would answer "not registered" about the person they have just registered.
          notePersonWrite();
          onCreated?.(created);
          return;
        }
        setAttaching({ created, error: null });
        void runAttach(created, staged);
      },
    },
  );

  const amend = useApiMutation<typeof apiRoutes.personPatch, AmendInput>(
    apiRoutes.personPatch,
    (input) => ({ params: { id: input.id }, body: input.body }),
    {
      onSuccess: (result) => {
        if (result.kind === 'confirmation_required') {
          setMissing(result.newlyMissing);
          return;
        }
        setMissing(null);
        remember();
        notePersonWrite();
        // The conflict is over: the next press of Save is a second deliberate write and quotes the
        // version the record now carries rather than the one a resolved conflict was merged against.
        setResolvedVersion(null);
        onAmended?.(result.person);
      },
    },
  );

  const pending = mode.kind === 'create' ? create : amend;
  const failure: unknown = pending.error;
  /**
   * The refusals the server attached to individual controls on the LAST submission.
   *
   * **Each one survives only while the control still holds the value it was about.** A rejected
   * mutation keeps its error until the next one settles, so a message like "there is already a
   * record with that number" would otherwise stay under the passport box while the operator was
   * typing a different number into it — the form telling them they are wrong about something they
   * have already changed. Comparing against the values as SUBMITTED is what expires it, at the
   * keystroke rather than at the next round trip.
   *
   * The client's own `problems` win where both have something to say, because those are recomputed
   * as the operator types and are therefore the more current of the two.
   */
  const [submittedValues, setSubmittedValues] = useState<FormValues>({});
  const reportedProblems = useMemo(
    () => serverFieldProblems(failure, definitions, locale),
    [failure, definitions, locale],
  );
  const serverProblems = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(reportedProblems).filter(([key]) => values[key] === submittedValues[key]),
      ),
    [reportedProblems, values, submittedValues],
  );
  /**
   * Every path a control TOOK, so the banner beneath lists only what no control is saying.
   *
   * Taken from what was reported rather than from what is still showing, and that difference is the
   * whole of it: a problem expires off its control the moment the operator changes that field, and
   * reading the filtered set here would have moved the sentence into the banner at exactly that
   * moment — the form insisting they are wrong about something they have already fixed.
   */
  const consumedPaths = useMemo(
    () => new Set(Object.keys(reportedProblems).map((key) => `body.fields.${key}`)),
    [reportedProblems],
  );
  const consentWanted = !consentDismissed && hasCode(failure, 'PERSON_CONSENT_REQUIRED');
  const blocked =
    Object.keys(problems).length > 0 || adhocFault !== null || (bandRequired && !band.ready);
  /**
   * True once this form has created somebody, which makes it terminal.
   *
   * The record exists from that moment: there is nothing left to submit, and nothing left to
   * cancel either — leaving through Cancel would abandon a person nobody has looked at, and from
   * the gate it would navigate away to the door.
   */
  const created = attaching !== null;

  const send = useCallback(
    (options: SubmitOptions) => {
      // What this attempt is about, so a refusal the server attaches to one control can be expired
      // the moment that control changes. State rather than a ref, because the memo above reads it
      // DURING a render and a ref read there is refused — rightly: a ref would let the comparison
      // see a value the render it is part of never had.
      setSubmittedValues({ ...values });
      const shared = {
        fields: submittableFields(definitions, values),
        adhocFields: submittableAdhoc(drafts),
        acceptIncomplete: options.acceptIncomplete,
        method,
        ...(options.consentAcknowledged === undefined
          ? {}
          : { consentAcknowledged: options.consentAcknowledged }),
      };
      if (mode.kind === 'create') {
        create.submit({
          ...shared,
          imageIds: [],
          ...(band.passCode === undefined ? {} : { passCode: band.passCode }),
        });
        return;
      }
      amend.submit({
        id: mode.person.id,
        // The situation travels with the fields, in one request: the service reads
        // `input.status ?? existing.status` and scopes its archive guard to the transition, so an
        // unchanged value is a no-op and a changed one writes its own audit row.
        body: { ...shared, status, version: resolvedVersion ?? mode.person.version },
      });
    },
    [amend, band, create, definitions, drafts, method, mode, resolvedVersion, status, values],
  );

  function submit(): void {
    setSubmitted(true);
    setConsentDismissed(false);
    if (blocked) {
      // A registration with no band used to be a dismissible warning raised here. It is now a
      // refusal the field itself renders (`BandField`'s `required`), because what the warning
      // produced was a person who resolves at no door, decided on the one screen where the operator
      // is standing in front of them and could have scanned one. The way past it is the break-glass
      // an administrator has to have turned on, which is a control on the field rather than a
      // dialog somebody clicks through.
      return;
    }
    send({ acceptIncomplete: false });
  }

  return (
    <form
      className="flex max-w-3xl flex-col gap-6"
      noValidate
      data-testid="person-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {!created && leaveControl}

      <h2 className="text-xl font-semibold tracking-tight">
        {mode.kind === 'create'
          ? t('person.form.createTitle')
          : t('person.form.amendTitle', { shortRef: mode.person.shortRef })}
      </h2>

      {/*
        **Inert once the record exists.** From the moment a create commits there is nothing left to
        submit — Save and Cancel are gone — and a field that still accepts typing is a field an
        operator corrects a typo in while reading the "the photograph was not saved" alert, whose
        edit nothing will ever send. A `fieldset` does it for every control inside at once,
        including the ones that take no `disabled` prop of their own.
      */}
      <fieldset disabled={created} className="contents">
        {bandRequired && (
          <BandField
            entry={band}
            required={submitted}
            offerNoBand={offerNoBand}
            noBandLabel={t(
              referenceRequiredWithoutBand
                ? 'person.form.noBandLabel'
                : 'person.form.emergencyLabel',
            )}
            noBandHint={t(
              referenceRequiredWithoutBand ? 'person.form.noBandHint' : 'person.form.emergencyHint',
            )}
            // The `fieldset` above disables every control inside it once the record exists; it
            // does NOT stop a camera that is already streaming, which only unmounting does.
            disabled={created}
          />
        )}

        {mode.kind === 'amend' && (
          <StatusField
            value={status}
            disabled={pending.submitting}
            onChange={(next) => {
              setStatus(next);
            }}
          />
        )}

        {/*
          One fieldset per section, which is the grouping element rather than a styled heading: a
          screen reader announces the legend with every control inside it, so an operator hears
          "Protección internacional, Registro PI" rather than "Registro PI" on its own — and on a
          thirty-five field form that is the difference between two similar questions.
        */}
        <div ref={fieldsRef} className="flex flex-col gap-6">
          {groups.map((group) => (
            <fieldset key={group.section ?? ''} data-section={group.section ?? ''}>
              <legend className="mb-3 text-sm font-semibold">
                {sectionHeading(group.section, t)}
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {group.fields.map((definition) => (
                  <div
                    key={definition.key}
                    className={definition.type === 'textarea' ? 'sm:col-span-2' : undefined}
                  >
                    <FieldControl
                      definition={definition}
                      value={values[definition.key] ?? ''}
                      problem={
                        submitted
                          ? (problems[definition.key] ?? serverProblems[definition.key] ?? null)
                          : null
                      }
                      today={today}
                      onChange={changeField(definition.key)}
                    />
                    {definition.key === BIRTH_DATE_KEY && derivedAge !== null && (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid="derived-age">
                        {t('person.form.age', { count: derivedAge })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
        </div>

        <AdhocFields
          drafts={drafts}
          suggestions={suggestions}
          problem={adhocFault}
          onChange={setDrafts}
        />

        {/*
        Last, because it is captured last: the completeness warning lists a missing photograph after
        the missing fields for the same reason, and an operator filling a form top to bottom reaches
        the camera when the typing is done.
      */}
        {mode.kind === 'create' && (
          <StagedPhotographs
            staged={staged}
            busy={pending.submitting || created}
            onChange={(next) => {
              // The form owns every preview this strip minted, so anything dropped here is released
              // here. One owner, or a preview is revoked twice or never.
              for (const one of staged) {
                if (!next.some((held) => held.id === one.id)) {
                  releaseStaged(one);
                }
              }
              setStaged(next);
            }}
            {...(camera ?? {})}
          />
        )}

        {!duplicatesDismissed && (
          <DuplicateGuard
            candidates={candidates}
            onDismiss={() => {
              setDuplicatesDismissed(true);
            }}
          />
        )}
      </fieldset>

      {failure !== null && failure !== undefined && !consentWanted && (
        <FailureAlert
          error={failure}
          title={t('person.form.saveFailed')}
          consumed={consumedPaths}
          labels={(path) => labelForFieldPath(path, definitions, locale)}
        />
      )}

      {/*
        The photograph, once the record it belongs to exists. Both states are terminal for the form:
        the person has been created either way, so neither offers a way back to editing it here.
      */}
      {attaching !== null && attaching.error === null && (
        <p role="status" className="text-sm text-muted-foreground" data-testid="attaching">
          {t('person.form.attaching')}
        </p>
      )}
      {attaching !== null && attaching.error !== null && (
        <Alert variant="destructive" role="alert" data-testid="attach-failed">
          <AlertTitle>{t('person.form.attachFailedTitle')}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            {describe(attaching.error)}
            <span className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAttaching({ created: attaching.created, error: null });
                  // Only what is LEFT: everything that committed was dropped as it landed.
                  void runAttach(attaching.created, staged);
                }}
              >
                {t('person.form.attachRetry')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  for (const one of staged) {
                    releaseStaged(one);
                  }
                  setStaged([]);
                  notePersonWrite();
                  onCreated?.(attaching.created);
                }}
              >
                {t('person.form.attachContinue')}
              </Button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/*
        A refused submission says so where the operator pressed, as well as on the field that caused
        it. The field's own message can be a screen away — and on an amendment the situation control
        is at the top, so a Save that quietly did nothing looked like a Save that worked.
      */}
      {submitted && blocked && !created && (
        <Alert variant="destructive" role="alert" data-testid="form-blocked">
          <AlertDescription>{t('person.form.blockedByProblems')}</AlertDescription>
        </Alert>
      )}

      {!created && (
        <div className="flex gap-2">
          <Button type="submit" disabled={pending.submitting}>
            {pending.submitting ? t('person.form.saving') : t('person.form.save')}
          </Button>
          {onCancel !== undefined && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('person.form.cancel')}
            </Button>
          )}
        </div>
      )}

      <CompletenessDialog
        open={missing !== null}
        missing={missing ?? []}
        definitions={definitions}
        confirming={pending.submitting}
        onConfirm={() => {
          setMissing(null);
          send({ acceptIncomplete: true });
        }}
        onCancel={() => {
          setMissing(null);
        }}
      />

      {/*
        A consent refusal is the server saying this installation's privacy notice has to be
        acknowledged before a record exists. It is not a control on the form, because whether it is
        required is a setting an operator cannot read; the refusal is what says so, and this is what
        answers it. `acceptIncomplete` is carried through, because the operator has already answered
        the completeness question by the time a consent refusal can arrive.
      */}
      {mode.kind === 'create' && (
        <Dialog
          open={consentWanted}
          onOpenChange={(next) => {
            if (!next) {
              setConsentDismissed(true);
            }
          }}
        >
          <DialogContent closeLabel={t('common.close')} data-testid="consent-dialog">
            <DialogHeader>
              <DialogTitle>{t('person.form.consentTitle')}</DialogTitle>
              <DialogDescription>{t('person.form.consentBody')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => {
                  setConsentDismissed(true);
                  send({ acceptIncomplete: true, consentAcknowledged: true });
                }}
              >
                {t('person.form.consentConfirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {mode.kind === 'amend' && (
        <ConflictResolution
          conflicted={amend.conflict !== null}
          personId={mode.person.id}
          definitions={definitions}
          mine={toFieldMap(definitions, values)}
          theirs={theirs}
          labelFor={(path) => {
            const definition = definitions.find((candidate) => candidate.key === path);
            return definition === undefined ? path : labelOf(definition, locale);
          }}
          onTheirs={(map, version) => {
            setTheirs(map);
            setResolvedVersion(version);
          }}
          onReload={() => {
            setTheirs(null);
            setResolvedVersion(null);
            amend.clearConflict();
            onCancel?.();
          }}
          onApply={(merged) => {
            setValues(valuesFromFieldMap(definitions, merged));
            setTheirs(null);
            amend.clearConflict();
          }}
        />
      )}
    </form>
  );
}

/**
 * What a conflict looks like when the record behind it could not be read.
 *
 * The dialog cannot be built without the saved values, and inventing them would be exactly the
 * silent overwrite it exists to prevent — so the operator is told plainly and offered the one
 * action that is always safe.
 */
function ConflictUnavailable({ onReload }: Readonly<{ onReload: () => void }>): ReactNode {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive" data-testid="conflict-unavailable">
      <AlertTitle>{t('conflict.unavailableTitle')}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        {t('conflict.unavailableBody')}
        <Button type="button" variant="outline" onClick={onReload}>
          {t('conflict.reload')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/**
 * The conflict dialog and the read that feeds it.
 *
 * **It is a component rather than an effect in the form**, so the read is mounted exactly while a
 * conflict is open and unmounted the moment it is answered — which is what stops a slow response
 * arriving after the operator has already resolved one and reopening a dialog they closed.
 *
 * The read is the ordinary record read, through the ordinary hook, so it carries AD-53's method and
 * shares the cache with every other read of the same record.
 */
function ConflictResolution({
  conflicted,
  ...rest
}: Readonly<{
  conflicted: boolean;
  personId: string;
  definitions: readonly FieldDefinition[];
  mine: FieldMap;
  theirs: FieldMap | null;
  labelFor: (path: string) => string;
  onTheirs: (map: FieldMap, version: number) => void;
  onReload: () => void;
  onApply: (merged: FieldMap) => void;
}>): ReactNode {
  // **Mounted per conflict rather than toggled**, which is the pattern `features/typed-confirm.tsx`
  // uses and the one `features/scan/anomaly.tsx` documents: it makes every piece of state below
  // fresh by construction, with no reset to remember to write and none to forget to update when a
  // member is added. It also keeps the read below out of the render of a form that is not in
  // conflict, which is every render but one.
  return conflicted ? <ConflictResolutionBody {...rest} /> : null;
}

function ConflictResolutionBody({
  personId,
  definitions,
  mine,
  theirs,
  labelFor,
  onTheirs,
  onReload,
  onApply,
}: Readonly<{
  personId: string;
  definitions: readonly FieldDefinition[];
  mine: FieldMap;
  theirs: FieldMap | null;
  labelFor: (path: string) => string;
  onTheirs: (map: FieldMap, version: number) => void;
  onReload: () => void;
  onApply: (merged: FieldMap) => void;
}>): ReactNode {
  const client = useApiClient();

  // **Read AFTER the refusal, and NOT through the shared cache.** A 409 means somebody saved
  // between this form loading and this operator pressing Save, so the record in the cache is by
  // definition the PRE-conflict one — and the record screen behind this dialog already has a live
  // observer on that key, so a second `useQuery` over it is handed that stale value synchronously.
  // Seeding `theirs` from it made the dialog list the operator's own edits against their own
  // pre-edit values and never mention the field the other person actually changed; `applyResolutions`
  // then starts from `theirs`, so confirming reverted their change with no conflict row having named
  // it. That is the silent overwrite this dialog exists to prevent.
  //
  // The version is taken from the SAME response, because the merge is computed against that
  // version's data: resubmitting under any other number either conflicts again or, worse, succeeds
  // while carrying values from a record that has moved on. If somebody writes again in between, the
  // next 409 opens this dialog again, which is correct.
  const [fresh, setFresh] = useState<PersonDetail | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    void client
      .request(apiRoutes.personGet, {
        params: { id: personId },
        query: { method: 'manual_search' },
      })
      .then(
        (record) => {
          if (live) {
            setFresh(record);
          }
        },
        () => {
          if (live) {
            setFailed(true);
          }
        },
      );
    return () => {
      live = false;
    };
  }, [client, personId]);

  useEffect(() => {
    if (fresh !== null && theirs === null) {
      onTheirs(toFieldMap(definitions, valuesFromRecord(definitions, fresh.fields)), fresh.version);
    }
  }, [definitions, fresh, onTheirs, theirs]);

  // A read that could not run is not a conflict anybody can resolve, so the operator is offered the
  // reload rather than a dialog built over half an answer.
  if (failed) {
    return <ConflictUnavailable onReload={onReload} />;
  }
  if (theirs === null) {
    return null;
  }

  return (
    <ConflictDialog
      open
      conflicts={conflictsBetween(mine, theirs)}
      theirs={theirs}
      mine={mine}
      labelFor={labelFor}
      onReload={onReload}
      onApply={onApply}
      onOpenChange={(next) => {
        if (!next) {
          onReload();
        }
      }}
    />
  );
}
